import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, Immobilie, VwgV2Vorbelegungdaten, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { OutbuildingSizeCategory } from '@ps20/bff-models';
import { AnyAction } from 'redux';
import { VwgActions } from '../vwgActions';
import { IAppState } from '../appState';
import { ResidentialUnits } from '../models';

export interface Outbuilding {
  available: boolean;
  sizeCategory: OutbuildingSizeCategory;
  totalArea: number;
  prefilled?: boolean;
}

export interface Gebaeude {
  baujahr: number;
  wohnflaeche: number;
  wohneinheiten: ResidentialUnits | null;
  nebengebaeude: Outbuilding;
  plzFaktorErdb?: number;
  plzFaktorSnd?: number;
  plzFaktorGla?: number;
  plzFaktorHws?: number;
}

function immobilieUebernehmen(
  sectionState: Gebaeude,
  action: AnyAction,
  appState: IAppState,
): Gebaeude {
  if (action.payload.appId !== App.VWG_V2 || action.payload.angebotsId !== appState.angebotsId) {
    return sectionState;
  }

  const immobilie = action.payload.immobilie as Immobilie;
  const prefilled = sectionState?.nebengebaeude?.prefilled;
  return {
    baujahr: immobilie.baujahr,
    wohnflaeche:
      immobilie.wohnflaeche != null && immobilie.wohnflaeche > 0 ? immobilie.wohnflaeche : null,
    wohneinheiten: null,
    nebengebaeude: prefilled ? sectionState.nebengebaeude : {
      available: immobilie.nebengebaeudeVorhanden ?? null,
      sizeCategory: null,
      totalArea: immobilie.nebengebaeudeQM > 0 ? immobilie.nebengebaeudeQM : null,
    } as Outbuilding,
  } as Gebaeude;
}

function outbuildingChanged(sectionState: Gebaeude, action: AnyAction): Gebaeude {
  return {
    ...sectionState,
    nebengebaeude: {
      ...sectionState.nebengebaeude,
      available: action.payload.available,
      sizeCategory: action.payload.available ? sectionState.nebengebaeude.sizeCategory : null,
      totalArea: action.payload.available ? sectionState.nebengebaeude.totalArea : null,
      prefilled: false,
    },
  };
}

function sizeCategoryChanged(sectionState: Gebaeude, action: AnyAction): Gebaeude {
  return {
    ...sectionState,
    nebengebaeude: {
      ...sectionState.nebengebaeude,
      sizeCategory: action.payload.sizeCategory,
      totalArea: action.payload.sizeCategory !== OutbuildingSizeCategory.CATEGORY_OVER_100 ? null : sectionState.nebengebaeude.totalArea,
    },
  };
}

function vorbelegungForBeibehaltung(
  sectionState: Gebaeude,
  action: AnyAction,
  appState: IAppState,
): Gebaeude {
  const actionPayload: VwgVorbelegungdaten = action.payload;
  if (actionPayload.forApp === App.VWG_V2 && actionPayload.angebotsId === appState.angebotsId) {
    return {
      ...sectionState,
      wohnflaeche: actionPayload.wohnflaeche,
      baujahr: actionPayload.baujahr,
      nebengebaeude: {
        available: actionPayload.merkmale?.nebengebaeudeQM != null,
        sizeCategory: null,
        totalArea: actionPayload.merkmale?.nebengebaeudeQM,
      },
      plzFaktorErdb: actionPayload.plzFaktorErdb,
      plzFaktorSnd: actionPayload.plzFaktorSnd,
      plzFaktorGla: actionPayload.plzFaktorGla,
      plzFaktorHws: actionPayload.plzFaktorHws,
    };
  }
  return sectionState;
}

function vorbelegungFinished(
  sectionState: Gebaeude,
  action: AnyAction,
  appState: IAppState,
): Gebaeude {
  const vorhandeneVorbelegungsdaten = action.payload.vorhandeneVorbelegungdaten as VwgV2Vorbelegungdaten;

  if (action.payload.appId === App.VWG_V2 && action.payload.angebotsId === appState.angebotsId) {
    const nebengebaeudeQM = vorhandeneVorbelegungsdaten.merkmale?.nebengebaeudeQM;
    const sizeCategory = nebengebaeudeQM != null ? getSizeCategoryByQM(nebengebaeudeQM) : null;
    sectionState = {
      ...sectionState,
      baujahr: vorhandeneVorbelegungsdaten.baujahr,
      wohnflaeche: vorhandeneVorbelegungsdaten.wohnflaeche,
      nebengebaeude: {
        available: vorhandeneVorbelegungsdaten.nebengebaeude,
        sizeCategory,
        totalArea: sizeCategory !== OutbuildingSizeCategory.CATEGORY_OVER_100 ? null : nebengebaeudeQM,
        prefilled: true,
      },
    };

  }

  return sectionState;
}

export const gebaeudeReducer = function (
  sectionState: Gebaeude = null,
  action: AnyAction,
  appState: IAppState,
): Gebaeude {
  switch (action.type) {
    case GlobalActions.IMMOBILIE_UEBERNEHMEN:
      return immobilieUebernehmen(sectionState, action, appState);
    case VwgActions.CONSTRUCTION_YEAR_CHANGED: {
      return {
        ...sectionState,
        baujahr: action.payload.constructionYear,
      };
    }
    case VwgActions.LIVING_AREA_CHANGED: {
      return {
        ...sectionState,
        wohnflaeche: action.payload.livingArea,
      };
    }
    case VwgActions.RESIDENTIAL_UNITS_CHANGED: {
      return {
        ...sectionState,
        wohneinheiten: action.payload.residentialUnits,
      };
    }
    case VwgActions.OUTBUILDING_CHANGED:
      return outbuildingChanged(sectionState, action);
    case VwgActions.SIZE_CATEGORY_CHANGED:
      return sizeCategoryChanged(sectionState, action);
    case VwgActions.TOTAL_AREA_CHANGED: {
      return {
        ...sectionState,
        nebengebaeude: {
          ...sectionState.nebengebaeude,
          totalArea: action.payload.totalArea,
        },
      };
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED:
      return vorbelegungForBeibehaltung(sectionState, action, appState);
    case GlobalActions.VORBELEGUNG_FINISHED:
      return vorbelegungFinished(sectionState, action, appState);
    default:
      return sectionState;
  }
};

function getSizeCategoryByQM(qm: number): OutbuildingSizeCategory {
  if (qm <= 25) {
    return OutbuildingSizeCategory.CATEGORY_1_25;
  }
  if (qm <= 50) {
    return OutbuildingSizeCategory.CATEGORY_26_50;
  }
  if (qm <= 100) {
    return OutbuildingSizeCategory.CATEGORY_51_100;
  }
  return OutbuildingSizeCategory.CATEGORY_OVER_100;
}
